import { useState } from "react";
import { Tooltip, TooltipProvider } from "../components/ui/Tooltip";
import { HelpCircle, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// ✅ Modern reusable modal component with enhanced UI/UX
const Modal = ({ open, onClose, title, children }) => {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Modal card */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 30 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 sm:p-8"
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{title}</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Close modal"
              >
                <X className="h-5 w-5 text-gray-500 hover:text-gray-700" />
              </button>
            </div>
            {/* Body */}
            <div className="text-gray-600 text-sm sm:text-base leading-relaxed">{children}</div>
            {/* Footer */}
            <div className="flex justify-end mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const PlanTypeInfoTooltip_v2 = () => {
  const [open, setOpen] = useState(false);

  const content = (
    <div className="max-w-xs sm:max-w-md text-sm sm:text-base space-y-4">
      <p className="text-white-600 leading-relaxed">
        When enrolling a new member, the plan availability depends on their member type:
      </p>
      <ul className="list-none space-y-3">
        <li className="grid grid-cols-[80px_1fr] items-start gap-2">
          <span className="inline-block px-2 py-1 text-xs font-semibold text-blue-800 bg-blue-100 rounded-full text-center">
            Principal
          </span>
          <span className="pt-1">
            This plan is available for new members with Principal type.
          </span>
        </li>
        <li className="grid grid-cols-[80px_1fr] items-start gap-2">
          <span className="inline-block px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full text-center">
            Dependent
          </span>
          <span className="pt-1">
            This plan is available only for new members with Dependent type.
          </span>
        </li>
        <li className="grid grid-cols-[80px_1fr] items-start gap-2">
          <span className="inline-block px-2 py-1 text-xs font-semibold text-purple-800 bg-purple-100 rounded-full text-center">
            Both
          </span>
          <span className="pt-1">
            This plan can be used by new members whether Principal or Dependent.
          </span>
        </li>
      </ul>
    </div>
  );

  return (
    <>
      <TooltipProvider>
        <Tooltip content={content}>
          <HelpCircle
            className="h-4 w-4 text-gray-500 cursor-pointer hover:text-gray-700 transition-colors"
            onClick={() => setOpen(true)}
          />
        </Tooltip>
      </TooltipProvider>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Plan Type Information"
      >
        {content}
      </Modal>
    </>
  );
};

export default PlanTypeInfoTooltip_v2;